import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import FolderService from '../../services/FolderService';
import { UiContext } from '../../context/UiContext';
import Checkbox from '../Checkbox';
import DotsLoader from '../DotsLoader';
import Tooltip from '../Tooltip';
import folderIcon from '../../assets/images/my-library/folder.svg';
import classes from './styles.module.scss';

const NODE_TYPES = {
  FOLDER: 'folder',
  MEETING: 'meeting',
};

const MAX_ITEMS_PER_LEVEL = 200;

const FolderTreeSelector = ({
  // Selection callbacks
  onFolderSelect,
  onFolderToggle,
  onMeetingToggle,
  // Optional callback to surface node names to parent
  onResourceNamesUpdate,
  // Optional set of folders to auto-expand (edit mode)
  autoExpandFolderIds,
  // Filtering/restrictions
  excludeFolderId,
  allowedFolderIds,
  currentFolderId,
  // Display options
  showCheckboxes = true,
  showMeetings = true,
  showRoot = false, // Show root folder option
  // Selection state (for Training Paths)
  selectedFolderIds,
  selectedMeetingIds,
  // Selection state (for Move mode)
  selectedFolderId,
  // Other props
  className,
  variant = 'default',
  isSubmitting = false,
  isPrefillPending = false,
}) => {
  const { notifyError } = useContext(UiContext);
  const [nodes, setNodes] = useState({});
  const [rootIds, setRootIds] = useState([]);
  const [isLoadingRoots, setIsLoadingRoots] = useState(false);
  const nodesRef = useRef(nodes);
  const hasAutoExpandedRef = useRef(false);

  const pushResourceNames = useCallback(
    (items) => {
      if (!onResourceNamesUpdate || !items?.length) {
        return;
      }
      const entries = {};
      items.forEach((item) => {
        if (item?.id && item?.name) {
          entries[item.id] = item.name;
        }
      });
      if (Object.keys(entries).length) {
        onResourceNamesUpdate(entries);
      }
    },
    [onResourceNamesUpdate]
  );

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const createFolderNode = useCallback((folder, parentId = null) => {
    return {
      id: folder.id,
      type: NODE_TYPES.FOLDER,
      name: folder.name,
      parentId,
      childrenIds: [],
      isExpanded: false,
      isLoading: false,
      childrenLoaded: false,
      hasChildren: true,
    };
  }, []);

  const createMeetingNode = useCallback((meeting, parentId) => {
    return {
      id: meeting.id,
      type: NODE_TYPES.MEETING,
      name: meeting.title,
      parentId,
      childrenIds: [],
      isExpanded: false,
      hasChildren: false,
    };
  }, []);

  const loadRootFolders = useCallback(async () => {
    try {
      setIsLoadingRoots(true);
      const data = await FolderService.getFolders({
        page: 1,
        limit: MAX_ITEMS_PER_LEVEL,
        sortField: 'name',
        sortOrder: 'ASC',
      });
      const rows = data?.rows || [];
      
      // Filter by allowedFolderIds if provided
      const filteredRows = allowedFolderIds
        ? rows.filter((folder) => allowedFolderIds.includes(folder.id))
        : rows;
      
      // Exclude excludeFolderId if provided
      const finalRows = excludeFolderId
        ? filteredRows.filter((folder) => folder.id !== excludeFolderId)
        : filteredRows;

      setNodes((prev) => {
        const next = { ...prev };
        // Add root node if showRoot is true
        if (showRoot) {
          const folderIds = finalRows.map((folder) => folder.id);
          // Create folders with root as parent
          finalRows.forEach((folder) => {
            const folderNode = createFolderNode(folder, ''); // Set root as parent
            next[folder.id] = folderNode;
          });
          next[''] = {
            id: '',
            type: NODE_TYPES.FOLDER,
            name: '/',
            parentId: null,
            childrenIds: folderIds,
            isExpanded: true, // Auto-expand root to show folders
            isLoading: false,
            childrenLoaded: true,
            hasChildren: folderIds.length > 0,
          };
        } else {
          // Normal mode: folders are top-level
          finalRows.forEach((folder) => {
            next[folder.id] = createFolderNode(folder);
          });
        }
        return next;
      });
      // If showRoot is true, only show root node; otherwise show all folders
      const rootNodeIds = showRoot ? [''] : finalRows.map((folder) => folder.id);
      setRootIds(rootNodeIds);

      // Surface names to parent for display in playlists/order list
      pushResourceNames(finalRows);
    } catch (error) {
      console.error(error);
      notifyError(
        error?.response?.data?.message ||
          'Unable to load folders. Please try again.'
      );
    } finally {
      setIsLoadingRoots(false);
    }
  }, [createFolderNode, notifyError, allowedFolderIds, excludeFolderId, showRoot]);

  useEffect(() => {
    loadRootFolders();
  }, [loadRootFolders]);

  const loadChildren = useCallback(
    async (folderId) => {
      setNodes((prev) => ({
        ...prev,
        [folderId]: {
          ...(prev[folderId] || {}),
          isLoading: true,
        },
      }));

      try {
        const data = await FolderService.getFolder({
          id: folderId,
          page: 1,
          limit: MAX_ITEMS_PER_LEVEL,
          sortField: 'updatedAt',
          sortOrder: 'DESC',
        });

        const subfolders = data?.subfolders || [];
        const meetings = showMeetings
          ? (data?.meetings || []).sort((a, b) => {
              const dateA = new Date(a.updatedAt || a.createdAt || 0);
              const dateB = new Date(b.updatedAt || b.createdAt || 0);
              return dateB - dateA;
            })
          : [];

        // Filter by allowedFolderIds if provided
        const filteredSubfolders = allowedFolderIds
          ? subfolders.filter((folder) => allowedFolderIds.includes(folder.id))
          : subfolders;

        // Exclude excludeFolderId if provided
        const finalSubfolders = excludeFolderId
          ? filteredSubfolders.filter((folder) => folder.id !== excludeFolderId)
          : filteredSubfolders;

        setNodes((prev) => {
          const next = { ...prev };
          const updatedFolder = { ...next[folderId] };

          const children = [];

          finalSubfolders.forEach((subfolder) => {
            next[subfolder.id] = createFolderNode(subfolder, folderId);
            children.push(subfolder.id);
          });

          if (showMeetings) {
            meetings.forEach((meeting) => {
              next[meeting.id] = createMeetingNode(meeting, folderId);
              children.push(meeting.id);
            });
          }

          updatedFolder.childrenIds = children;
          updatedFolder.childrenLoaded = true;
          updatedFolder.isExpanded = true;
          updatedFolder.isLoading = false;
          next[folderId] = updatedFolder;
          return next;
        });

        // Surface names for newly loaded items
        pushResourceNames([
          ...finalSubfolders,
          ...(showMeetings
            ? meetings.map((m) => ({ id: m.id, name: m.title }))
            : []),
        ]);
      } catch (error) {
        console.error(error);
        notifyError(
          error?.response?.data?.message ||
            'Unable to load folder contents. Please try again.'
        );
        setNodes((prev) => ({
          ...prev,
          [folderId]: {
            ...prev[folderId],
            isLoading: false,
          },
        }));
      }
    },
    [createFolderNode, createMeetingNode, notifyError, showMeetings, allowedFolderIds, excludeFolderId]
  );

  useEffect(() => {
    if (
      !showCheckboxes ||
      isPrefillPending ||
      hasAutoExpandedRef.current ||
      !autoExpandFolderIds ||
      autoExpandFolderIds.size === 0 ||
      rootIds.length === 0
    ) {
      return;
    }

    const buildPath = async (folderId) => {
      try {
        const response = await FolderService.getFolder({
          id: folderId,
          page: 1,
          limit: 1,
          sortField: 'title',
          sortOrder: 'ASC',
        });
        const parents = response?.parentFolders || [];
        return [...parents.map((folder) => folder.id), folderId];
      } catch (error) {
        console.error(error);
        return [folderId];
      }
    };

    const expandFolderPath = async (folderId) => {
      const pathIds = await buildPath(folderId);

      const processPath = async (index, previousId) => {
        if (index >= pathIds.length) {
          return;
        }

        const currentId = pathIds[index];

        if (previousId) {
          const parentNode = nodesRef.current[previousId];
          if (!parentNode || !parentNode.childrenLoaded) {
            await loadChildren(previousId);
          }
        }

        const currentNode = nodesRef.current[currentId];
        if (currentNode && !currentNode.childrenLoaded) {
          await loadChildren(currentId);
        }

        setNodes((prev) => {
          const node = prev[currentId];
          if (!node || node.isExpanded) {
            return prev;
          }
          return {
            ...prev,
            [currentId]: {
              ...node,
              isExpanded: true,
            },
          };
        });

        await processPath(index + 1, currentId);
      };

      await processPath(0, null);
    };

    (async () => {
      await Promise.all(
        Array.from(autoExpandFolderIds).map((folderId) =>
          expandFolderPath(folderId)
        )
      );
      hasAutoExpandedRef.current = true;
    })();
  }, [autoExpandFolderIds, isPrefillPending, loadChildren, showCheckboxes, rootIds.length, pushResourceNames]);

  const toggleFolder = useCallback(
    (nodeId) => {
      const node = nodes[nodeId];
      if (!node || node.type !== NODE_TYPES.FOLDER) {
        return;
      }

      if (!node.childrenLoaded && !node.isLoading) {
        loadChildren(nodeId);
        return;
      }

      setNodes((prev) => ({
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          isExpanded: !prev[nodeId].isExpanded,
        },
      }));
    },
    [loadChildren, nodes]
  );

  const isAncestorSelected = useCallback(
    (node) => {
      if (!node?.parentId || !selectedFolderIds) {
        return false;
      }
      if (selectedFolderIds.has(node.parentId)) {
        return true;
      }
      const parentNode = nodes[node.parentId];
      if (!parentNode) {
        return false;
      }
      return isAncestorSelected(parentNode);
    },
    [nodes, selectedFolderIds]
  );

  const handleFolderClick = useCallback(
    (nodeId, nodeName) => {
      if (showCheckboxes && onFolderToggle) {
        // Training Paths mode: toggle checkbox
        onFolderToggle(nodeId);
      } else if (onFolderSelect) {
        // Move mode: single selection
        // Convert root node (empty string) to null for API
        onFolderSelect(nodeId === '' ? null : nodeId, nodeName);
      }
    },
    [showCheckboxes, onFolderToggle, onFolderSelect]
  );

  const handleMeetingClick = useCallback(
    (meetingId) => {
      if (showCheckboxes && onMeetingToggle) {
        // Training Paths mode: toggle checkbox
        onMeetingToggle(meetingId);
      }
    },
    [showCheckboxes, onMeetingToggle]
  );

  const renderNodes = useCallback(
    (nodeIds, depth = 0) => {
      return nodeIds.map((nodeId) => {
        const node = nodes[nodeId];
        if (!node) {
          return null;
        }

        // Skip meetings if not showing them
        if (node.type === NODE_TYPES.MEETING && !showMeetings) {
          return null;
        }

        const ancestorSelected = showCheckboxes ? isAncestorSelected(node) : false;
        const isFolderSelected = selectedFolderIds?.has(node.id) || false;
        const isMeetingSelected = selectedMeetingIds?.has(node.id) || false;
        const isChecked =
          showCheckboxes &&
          (ancestorSelected ||
            (node.type === NODE_TYPES.FOLDER && isFolderSelected) ||
            (node.type === NODE_TYPES.MEETING && isMeetingSelected));

        const isDisabled =
          showCheckboxes &&
          (isSubmitting || isPrefillPending || ancestorSelected);
        const isCurrentFolder = currentFolderId === node.id;
        // For root node (id === ''), selectedFolderId should be '' (not null) to be selected
        const isSelected = (!showCheckboxes && selectedFolderId === node.id) || (showCheckboxes && ancestorSelected);
        const isCurrentFolderDisabled = !showCheckboxes && isCurrentFolder;

        return (
          <li key={node.id} className={classes.nodeItem}>
            <div
              className={classes.nodeRow}
              style={{ paddingLeft: `${depth * 24}px` }}
            >
              {showCheckboxes && (
                <Checkbox
                  isDisabled={isDisabled}
                  isChecked={isChecked}
                  onClick={() => {
                    if (isDisabled || (showCheckboxes && ancestorSelected)) {
                      return;
                    }
                    if (node.type === NODE_TYPES.FOLDER) {
                      handleFolderClick(node.id, node.name);
                    } else {
                      handleMeetingClick(node.id);
                    }
                  }}
                />
              )}
              {node.type === NODE_TYPES.FOLDER && (
                node.id !== '' ? (
                  <button
                    type="button"
                    className={classes.folderIconButton}
                    onClick={() => toggleFolder(node.id)}
                    aria-label={`Toggle ${node.name} folder`}
                  >
                    <img src={folderIcon} alt="Folder" className={classes.folderIcon} />
                  </button>
                ) : (
                  <div className={classes.folderIconButton} style={{ cursor: 'default', pointerEvents: 'none' }}>
                    <img src={folderIcon} alt="Root" className={classes.folderIcon} />
                  </div>
                )
              )}
              {isCurrentFolderDisabled ? (
                <Tooltip text="Cannot move to current location">
                  <button
                    type="button"
                    className={`${classes.nodeLabel} ${isSelected ? classes.selectedFolder : ''}`}
                    disabled
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {node.name}
                  </button>
                </Tooltip>
              ) : (
                <button
                  type="button"
                  className={`${classes.nodeLabel} ${isSelected ? classes.selectedFolder : ''}`}
                  onClick={() => {
                    if (node.type === NODE_TYPES.FOLDER) {
                      if (!showCheckboxes && onFolderSelect) {
                        // Move mode: select folder
                        handleFolderClick(node.id, node.name);
                      } else {
                        // Training Paths mode: toggle folder
                        toggleFolder(node.id);
                      }
                    } else {
                      // For videos, toggle checkbox instead of navigating
                      handleMeetingClick(node.id);
                    }
                  }}
                >
                  {node.name}
                </button>
              )}
            </div>
            {node.isExpanded && (
              <>
                {node.childrenIds.length > 0 && (
                  <ul className={classes.childrenList}>
                    {renderNodes(node.childrenIds, depth + 1)}
                  </ul>
                )}
                {node.childrenIds.length === 0 && node.childrenLoaded && (
                  <div className={classes.emptyFolderMessage}>
                    {showMeetings ? 'No videos in this folder' : 'No subfolders in this folder'}
                  </div>
                )}
              </>
            )}
          </li>
        );
      });
    },
    [
      handleFolderClick,
      handleMeetingClick,
      isAncestorSelected,
      isSubmitting,
      isPrefillPending,
      nodes,
      selectedFolderIds,
      selectedMeetingIds,
      showCheckboxes,
      showMeetings,
      toggleFolder,
      currentFolderId,
      selectedFolderId,
    ]
  );

  return (
    <div
      className={`${classes.treeContainer} ${
        variant === 'compact' ? classes.compact : ''
      } ${className || ''}`}
    >
      {isLoadingRoots && (
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      )}
      {rootIds.length === 0 && !isLoadingRoots && (
        <p className={classes.emptyState}>
          We couldn&apos;t find any folders that you can access.
        </p>
      )}
      <ul className={classes.tree}>
        {renderNodes(rootIds)}
      </ul>
    </div>
  );
};

export default FolderTreeSelector;
